import React from 'react';
import { Panel } from '../../components/ui/Panel';
import { PanelHeader } from '../../components/ui/PanelHeader';
import { Button } from '../../components/ui/Button';
import { MetricStat } from '../../components/admin/MetricStat';
import { platformIncidents, workerRegions } from '../../data/adminPlatform';
import { formatDateTime, relativeTime } from '../../utils/format';
const regionTone = {
  ok: {
    rail: 'bg-healthy',
    label: 'Operational',
    chip: 'bg-healthy-soft text-healthy-ink border-healthy-border'
  },
  degraded: {
    rail: 'bg-warn',
    label: 'Degraded',
    chip: 'bg-warn-soft text-warn-ink border-warn-border'
  },
  offline: {
    rail: 'bg-critical',
    label: 'Offline',
    chip: 'bg-critical-soft text-critical-ink border-critical-border'
  }
} as const;
export function AdminSystem() {
  const totalThroughput = workerRegions.reduce((sum, region) => sum + region.checksPerMinute, 0);
  const totalQueue = workerRegions.reduce((sum, region) => sum + region.queueDepth, 0);
  return <div className="space-y-5" data-id="element-762" data-mp="7nu7r1yzlzrr">
      <header data-id="element-763" data-mp="cflnehglul34">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-764" data-mp="cgezzz28w4t6">
          System health
        </h1>
        <p className="mt-1 text-sm text-ink-muted" data-id="element-765" data-mp="3uunx3mxz975h">
          The scheduler, the workers, and the outbound mail path.
        </p>
      </header>

      <Panel data-id="element-766" data-mp="2nzguyf7pa6p">
        <div className="grid divide-y divide-line sm:grid-cols-2 sm:divide-y-0 lg:grid-cols-4 lg:divide-x" data-id="element-767" data-mp="2nprnd1zr722">
          <MetricStat label="Throughput" value={totalThroughput.toLocaleString()} detail="Checks per minute" emphasis data-id="element-768" data-mp="verbhrgmpqrx" />
          <MetricStat label="Queue depth" value={String(totalQueue)} detail="Across all regions" tone={totalQueue > 200 ? 'warn' : 'default'} data-id="element-769" data-mp="hh2mq8jfxzhl" />
          <MetricStat label="Sweep completed" value="08:04" detail="Certificates, all accounts" data-id="element-770" data-mp="327jxjxhnll13" />
          <MetricStat label="Email delivery" value="99.4%" detail="Last 24 hours" data-id="element-771" data-mp="1cobo150zvumt" />
        </div>
      </Panel>

      <Panel as="section" data-id="element-772" data-mp="17rbg0okl4ht2">
        <PanelHeader title="Check workers" description="Queue depth above 100 means customers are getting late pings." level={2} action={<Button variant="secondary" size="sm" data-id="element-774" data-mp="txkikek4oodp">
              Drain and rebalance
            </Button>} data-id="element-773" data-mp="12xqptip8gs4f" />
        <ul className="divide-y divide-line" data-id="element-775" data-mp="oy7vbspchfal">
          {workerRegions.map(region => {
          const tone = regionTone[region.state];
          return <li key={region.id} className="flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-4" data-id="element-776" data-mp="k17mja141yfs">
                <span className={`h-10 w-1 shrink-0 rounded-full ${tone.rail}`} aria-hidden="true" data-id="element-777" data-mp="2ece2w8x7jap3" />
                <span className="min-w-0 flex-1" data-id="element-778" data-mp="3s9gpukl6jkpo">
                  <span className="flex flex-wrap items-center gap-2" data-id="element-779" data-mp="3s8nqw5sgpzi3">
                    <span className="text-sm font-medium text-ink" data-id="element-780" data-mp="12madhpqadp2">
                      {region.name}
                    </span>
                    <span className="font-mono text-[11px] text-ink-soft" data-id="element-781" data-mp="5v5ocfsbvaue">
                      {region.code}
                    </span>
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone.chip}`} data-id="element-782" data-mp="9xag18z7txee">
                      {tone.label}
                    </span>
                  </span>
                  <span className="mt-1.5 block font-mono text-[11px] text-ink-soft" data-id="element-783" data-mp="3iaztt97dwfwr">
                    heartbeat {relativeTime(region.lastHeartbeatAt)}
                  </span>
                </span>

                <dl className="flex gap-6" data-id="element-784" data-mp="xk0gi5vx1jww">
                  <div data-id="element-785" data-mp="xn4rpn7stfx2">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-786" data-mp="12jv6m77va5gw">
                      Queue
                    </dt>
                    <dd className={`mt-1 font-mono text-[15px] font-medium ${region.queueDepth > 100 ? 'text-warn-ink' : 'text-ink'}`} data-id="element-787" data-mp="113hoc6dg4cq3">
                      {region.queueDepth}
                    </dd>
                  </div>
                  <div data-id="element-788" data-mp="1645haxl8j3my">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-789" data-mp="1k1k9ga2vhpim">
                      Per min
                    </dt>
                    <dd className="mt-1 font-mono text-[15px] font-medium text-ink" data-id="element-790" data-mp="1f27lfm09mceg">
                      {region.checksPerMinute.toLocaleString()}
                    </dd>
                  </div>
                  <div data-id="element-791" data-mp="1ot298ut5tk20">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-792" data-mp="1ouwktiijjztf">
                      p95
                    </dt>
                    <dd className={`mt-1 font-mono text-[15px] font-medium ${region.p95Ms > 1000 ? 'text-warn-ink' : 'text-ink'}`} data-id="element-793" data-mp="1xu9x0iy1v2hu">
                      {region.p95Ms}ms
                    </dd>
                  </div>
                </dl>
              </li>;
        })}
        </ul>
      </Panel>

      <Panel as="section" data-id="element-794" data-mp="2cew5pyjbriig">
        <PanelHeader title="Incident log" description="Anything that delayed a check or an email, whether or not customers noticed." level={2} data-id="element-795" data-mp="2ce49clhwlgr4" />
        <ul className="divide-y divide-line" data-id="element-796" data-mp="23g0ycj4jo2dk">
          {platformIncidents.map(incident => <li key={incident.id} className="px-5 py-4" data-id="element-797" data-mp="1oro01fhzi7fw">
              <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-798" data-mp="1tp6c3erat2s4">
                <div className="min-w-0" data-id="element-799" data-mp="1tn3bk0sirr95">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-800" data-mp="1fpeu72xnv28u">
                    <h3 className="text-[13px] font-semibold text-ink" data-id="element-801" data-mp="1kohygl5th4fr">
                      {incident.title}
                    </h3>
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${incident.resolvedAt ? 'border-paused-border bg-paused-soft text-paused-ink' : incident.severity === 'critical' ? 'border-critical-border bg-critical-soft text-critical-ink' : 'border-warn-border bg-warn-soft text-warn-ink'}`} data-id="element-802" data-mp="3meytx1ihcw2d">
                      {incident.resolvedAt ? 'Resolved' : 'Open'}
                    </span>
                  </div>
                  <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-ink-muted" data-id="element-803" data-mp="8tlanrkdlu6">
                    {incident.body}
                  </p>
                </div>
                <p className="shrink-0 text-right font-mono text-[11px] text-ink-soft" data-id="element-804" data-mp="2kxpszypecg47">
                  {formatDateTime(incident.startedAt)}
                  <span className="mt-1 block" data-id="element-805" data-mp="vfsvftcc73s4">{incident.affected}</span>
                </p>
              </div>
            </li>)}
        </ul>
      </Panel>
    </div>;
}